/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';

import { FileWithDetectionsViewer } from '@vlabs/monokit/features/file-with-detections-viewer/FileWithDetectionsViewer';
import type { TBbox } from '@vlabs/monokit/shared/ui/canvas';
import { Control } from '@vlabs/uikit';
import { connect } from 'react-redux';

import { DEFAULT_BBOX_STYLES } from './BboxStyles';
import type { TDetection, PhotoUploadFormProps } from './types';
import { imageTypes } from '../../config/imageTypes.js';
import { selectMaxImageSize } from '../../selectors/appSelectors.js';
import validate from '../../validators/index.js';

function PhotoUploadFormComponent({
  maxImageSize,
  file,
  defaultFile = undefined,
  detections = [],
  bboxStyles = undefined,
  selectedStateProps = undefined,
  onFileSelect,
  onSelectSampleId,
  onResetFile,
  errorProps,
  disabledWhenDetections = true,
}: PhotoUploadFormProps): React.JSX.Element {
  const [internalSelected, setInternalSelected] = useState<TDetection[]>([]);
  const [selected, setSelected] = selectedStateProps
    ? [selectedStateProps.selectedDetections, selectedStateProps.setSelectedDetections]
    : [internalSelected, setInternalSelected];

  const isSelected = useCallback((detectionId: string) => {
    return selected?.some((detection) => detection.id === detectionId);
  }, [selected]);

  const onBBoxClick = useCallback((Bbox: TBbox[] | null): void => {
    if (!Bbox?.length) return;
    const id = Bbox[0]?.id;
    const detection = detections.find((d) => d.id === id);
    if (!detection) return;
    setSelected([detection]);
  }, [detections]);

  const defaultFiles = useMemo(() => {
    if (!defaultFile?.raw) return undefined;
    const dt = new DataTransfer();
    dt.items.add(defaultFile.raw);
    return dt.files;
  }, [defaultFile]);

  useEffect(() => {
    if (!onSelectSampleId) return;
    onSelectSampleId(selected?.map((d) => d.id));
  }, [selected]);

  useEffect(() => {
    if (detections?.length === 1) {
      const detection = detections[0];
      if (detection) setSelected([detection]);
    }
  }, [detections]);

  const coloredBboxList = detections?.map((detection) => ({
    ...detection.rect,
    id: detection.id,
    isSelected: isSelected(detection.id),
    style: bboxStyles ?? DEFAULT_BBOX_STYLES,
    key: detection.id,
  }));

  return (
    <Control.FileDropzone
      data-testid="fileDropzone"
      defaultFile={defaultFiles}
      id="file"
      onChangeValue={onFileSelect}
      {...(errorProps?.register?.('file', {
        required: !file && validate.required(),
      }))
      }
      disabledWhenDetections={disabledWhenDetections}
      error={errorProps.errors.file?.message ?? errorProps.errors.message}
      fileViewer={file ? {
        [file.raw?.type]: <FileWithDetectionsViewer
          coloredBboxList={coloredBboxList}
          file={file}
          onBBoxClick={onBBoxClick}
        />,
      } : {}}
      localFile={file}
      maxImageSize={maxImageSize ?? undefined}
      onlyImages
      onReset={onResetFile}
      supportedImageTypes={imageTypes}
    />
  );
}

export { PhotoUploadFormComponent };

export const PhotoUploadForm = connect(
  (state) => ({
    maxImageSize: selectMaxImageSize(state),
  }),
)(PhotoUploadFormComponent);
